import { Box, Drawer, Typography, IconButton, Alert } from '@mui/material';
import React, { useState } from 'react';
import { useTheme, useMediaQuery, alpha } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { FunnelCart } from 'src/ts/types/cart';
import MenuMealsSummary from './MenuMealsSummary';
import MenuPriceSummary from '../MenuPriceSummary';
import { Plan } from 'src/services/cart-order/plans-service';
import SummaryDrawerActions from './SummaryDrawerActions';
import { useApolloClient } from '@apollo/client/react';
import { emptyCartDelivery } from './empty-cart-delivery';
import logger from 'logger';

interface SummaryDrawerProps {
  open: boolean;
  onClose: () => void;
  cart: FunnelCart;
  setCart: React.Dispatch<React.SetStateAction<FunnelCart | null>>;
  addMealToCart: (productVariantId: number, cartDeliveryId: number) => void;
  removeMealFromCart: (
    productVariantId: number,
    cartDeliveryId: number,
  ) => void;
  onContinueButtonClick: () => void;
  soldOutItemVariantMap: Record<string, Array<number>>;
  selectedPlan?: Plan;
  cartCreatedFromPersonalizedPlan?: boolean;
  expandedDeliveryId: number | null;
  setExpandedDeliveryId: React.Dispatch<React.SetStateAction<number | null>>;
}

const SummaryDrawer: React.FC<SummaryDrawerProps> = ({
  open,
  onClose,
  cart,
  setCart,
  addMealToCart,
  removeMealFromCart,
  onContinueButtonClick,
  soldOutItemVariantMap,
  selectedPlan,
  cartCreatedFromPersonalizedPlan,
  expandedDeliveryId,
  setExpandedDeliveryId,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const apolloClient = useApolloClient();
  const [isClearingCart, setIsClearingCart] = useState(false);

  const clearCart = async () => {
    setIsClearingCart(true);
    for (const delivery of cart.cartDeliveries) {
      if (delivery.lineItems.length === 0) continue;

      try {
        const result = await emptyCartDelivery({
          apolloClient,
          cartUUID: cart.uuid,
          cartDeliveryId: delivery.id,
        });

        if (result.success) {
          setCart(result.cart);
        } else {
          logger.error({
            msg: 'FailedToEmptyCartDelivery',
            extra: {
              cartUUID: cart.uuid,
              cartDeliveryId: delivery.id,
              error: result.error,
            },
          });
        }
      } catch (error) {
        logger.error({
          msg: 'FailedToEmptyCartDelivery',
          noticable: error as Error,
          extra: {
            cartUUID: cart.uuid,
            cartDeliveryId: delivery.id,
          },
        });
      }
    }
    setIsClearingCart(false);
  };

  const totalNumMeals = cart.cartDeliveries.reduce(
    (total, delivery) =>
      total +
      delivery.lineItems.reduce(
        (deliveryTotal, lineItem) => deliveryTotal + lineItem.quantity,
        0,
      ),
    0,
  );

  const modalMargins = isSmallScreen ? 1 : 3;
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            // The mobile styles treat this as a real drawer, it's coming from
            // the right side of the screen. On wider displays, the drawer is
            // slightly offset from the right side of the screen
            width: isSmallScreen ? '90vw' : 450,
            maxWidth: '100%',
            height: isSmallScreen ? '100dvh' : 'calc(100vh - 48px)',
            top: isSmallScreen ? 0 : 24,
            right: isSmallScreen ? 0 : 24, // Offset drawer from right on large screens
            borderRadius: isSmallScreen ? 'none' : 3,
            p: modalMargins,
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" sx={{ p: 1 }}>
            Order summary
          </Typography>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
        {selectedPlan && !cart.modifiedDuringFunnel && (
          <Alert
            severity="info"
            icon={<AddShoppingCartIcon fontSize="inherit" />}
            sx={{
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              mb: 1,
              fontWeight: 500,
              '& .MuiAlert-icon': {
                color: theme.palette.primary.main,
              },
            }}
          >
            These meals were pre-selected from the{' '}
            <Box component="span" sx={{ fontWeight: 800 }}>
              {selectedPlan.displayName}
            </Box>{' '}
            plan.
          </Alert>
        )}

        {cartCreatedFromPersonalizedPlan && !cart.modifiedDuringFunnel && (
          <Alert
            severity="info"
            icon={<AddShoppingCartIcon fontSize="inherit" />}
            sx={{
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              mb: 1,
              fontWeight: 500,
              '& .MuiAlert-icon': {
                color: theme.palette.primary.main,
              },
            }}
          >
            These meals were hand-picked based on your preferences.
          </Alert>
        )}

        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            containerType: 'size',
          }}
        >
          {cart.cartDeliveries.map((cartDelivery) => (
            <MenuMealsSummary
              key={cartDelivery.id}
              cartDelivery={cartDelivery}
              expanded={expandedDeliveryId === cartDelivery.id}
              onToggle={() =>
                setExpandedDeliveryId(
                  expandedDeliveryId === cartDelivery.id
                    ? null
                    : cartDelivery.id,
                )
              }
              addMealToCart={addMealToCart}
              removeMealFromCart={removeMealFromCart}
              totalDeliveries={cart.cartDeliveries.length}
              displayError={
                cart.cartError?.validation?.quantity?.find(
                  (quantityError) =>
                    quantityError.deliveryId === cartDelivery.id,
                )?.message
              }
              // This value is needed to be passed into child components to set height. There's some
              // CSS math going on to have the body of the expanded accordian fit whatever remaining
              // space is available while reserving space to show errors and the other accoridan titles
              totalDisplayErrors={
                cart.cartError?.validation?.quantity?.length ?? 0
              }
              soldOutVariantIds={
                soldOutItemVariantMap[cartDelivery.deliveryDate] ?? []
              }
            />
          ))}
        </Box>
        <Box sx={{ flexShrink: 0, mt: 1 }}>
          <MenuPriceSummary
            cart={cart}
            setCart={setCart}
            cartLedger={cart.cartLedger!}
            totalNumMeals={totalNumMeals}
          />
        </Box>
        <SummaryDrawerActions
          modalMargins={modalMargins}
          onContinueButtonClick={onContinueButtonClick}
          disabled={!!cart.cartError?.validation}
          clearCart={selectedPlan ? clearCart : undefined}
          clearCartDisabled={isClearingCart || totalNumMeals === 0}
        />
      </Box>
    </Drawer>
  );
};

export default SummaryDrawer;
